import { useCallback, useEffect, useRef, useState } from "react";

interface UseAudioPlaybackApi {
  isPlaying: boolean;
  progressMs: number;
  durationMs: number;
  toggle: () => void;
  play: () => void;
  pause: () => void;
}

export const useAudioPlayback = (
  audioUrl: string | null,
): UseAudioPlaybackApi => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progressMs, setProgressMs] = useState(0);
  const [durationMs, setDurationMs] = useState(0);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
        audioRef.current = null;
      }
      setIsPlaying(false);
      setProgressMs(0);
      setDurationMs(0);
    };
  }, [audioUrl]);

  const ensureAudio = useCallback((): HTMLAudioElement | null => {
    if (audioRef.current) return audioRef.current;
    if (!audioUrl) return null;
    const el = new Audio(audioUrl);
    el.addEventListener("loadedmetadata", () => {
      setDurationMs(el.duration * 1000);
    });
    el.addEventListener("timeupdate", () => {
      setProgressMs(el.currentTime * 1000);
    });
    el.addEventListener("ended", () => {
      setIsPlaying(false);
      setProgressMs(0);
    });
    audioRef.current = el;
    return el;
  }, [audioUrl]);

  const play = useCallback(() => {
    const el = ensureAudio();
    if (!el) return;
    void el.play();
    setIsPlaying(true);
  }, [ensureAudio]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    setIsPlaying(false);
  }, []);

  const toggle = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, play, pause]);

  return { isPlaying, progressMs, durationMs, toggle, play, pause };
};
